/**
 * CoPilot Brain Companion — response envelopes (generative UI).
 *
 * The closed set of structured cards the model may emit and every frontend
 * renders natively (PRD §14, TECH §3.2). The model chooses an envelope; the
 * FE owns the rendering — no model-generated markup, ever. Unknown
 * type/version degrades to `text` on the client.
 *
 * `escalation_card` is DESIGNED BUT OFF (full-AI posture, D11): the shape
 * ships so a future human-handoff flip is additive, but no producer emits it
 * in SR2 and FEs render it as text if ever encountered.
 */

/** Where a grounded answer came from — powers the trust affordance (PRD §11). */
export interface CoPilotSource {
  origin: 'kb' | 'user_data' | 'tool' | 'case_base';
  /** Human-readable label, e.g. the KB note title or "your CheckUp result". */
  label: string;
  /** KB note id when origin = 'kb'. */
  noteId?: string;
  /** Sample size when origin = 'case_base' (provenance honesty — always shown). */
  n?: number;
}

/** SR2 host commands are navigate/openEntity ONLY (TECH §2; full bus is R3+). */
export interface CoPilotEnvelopeHostCommand {
  command: 'navigate' | 'openEntity';
  /** Route path (navigate) or entity route base (openEntity). */
  target: string;
  entityType?: string;
  entityId?: string;
}

interface CoPilotEnvelopeBase {
  /** Per-envelope schema version; FE falls back to text on unknown versions. */
  version: number;
  source?: CoPilotSource;
  hostCommand?: CoPilotEnvelopeHostCommand;
}

export interface CoPilotTextEnvelope extends CoPilotEnvelopeBase {
  type: 'text';
  text: string;
}

export interface CoPilotTaskCardEnvelope extends CoPilotEnvelopeBase {
  type: 'task_card';
  taskId: string;
  title: string;
  hero: string;
  status: string;
  dueDate?: string;
}

export interface CoPilotChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

export interface CoPilotChecklistEnvelope extends CoPilotEnvelopeBase {
  type: 'checklist';
  title: string;
  items: CoPilotChecklistItem[];
}

export interface CoPilotScoreComponent {
  key: string;
  label: string;
  /** Backend-provided value — never computed or restated by the model (rule #9). */
  value: number;
  narrative: string;
}

export interface CoPilotScoreExplainerEnvelope extends CoPilotEnvelopeBase {
  type: 'score_explainer';
  scoreLabel: string;
  scoreValue: number;
  components: CoPilotScoreComponent[];
  narrative: string;
}

export interface CoPilotKitItemEnvelope extends CoPilotEnvelopeBase {
  type: 'kit_item';
  kitItemId: string;
  title: string;
  status: string;
  summary: string;
}

export interface CoPilotComparisonTableEnvelope extends CoPilotEnvelopeBase {
  type: 'comparison_table';
  title?: string;
  columns: string[];
  rows: string[][];
}

export interface CoPilotDocumentClause {
  title: string;
  explanation: string;
  /** Present when the clause was translated to the user's locale (D9). */
  translated?: string;
}

export interface CoPilotDocumentSummaryEnvelope extends CoPilotEnvelopeBase {
  type: 'document_summary';
  fileName: string;
  summary: string;
  clauses?: CoPilotDocumentClause[];
  sourceLanguage?: string;
  targetLanguage?: string;
}

export interface CoPilotCaseInsightEnvelope extends CoPilotEnvelopeBase {
  type: 'case_insight';
  insight: string;
  /** Anonymized sample size — REQUIRED and always rendered (PRD C8). */
  n: number;
  corridor?: string;
  /** Small-n or freshness caveat, localized. */
  caveat?: string;
}

/** The C7 healing fallback: a pre-diagnosed, submittable problem report. */
export interface CoPilotProblemReportEnvelope extends CoPilotEnvelopeBase {
  type: 'problem_report';
  title: string;
  diagnostic: {
    area: string;
    observedState: string;
    details: string;
  };
  submittable: boolean;
}

import type { CoPilotActionProposal } from './proposal';

export interface CoPilotActionProposalEnvelope extends CoPilotEnvelopeBase {
  type: 'action_proposal';
  proposal: CoPilotActionProposal;
}

/** DESIGNED, OFF in SR2 (D11) — no producer emits this; kept for an additive flip. */
export interface CoPilotEscalationCardEnvelope extends CoPilotEnvelopeBase {
  type: 'escalation_card';
  reasonKey: string;
  routeLabel: string;
  routeTarget: string;
}

/** One web citation behind a `web_search` answer (BL-213 v2). */
export interface CoPilotWebCitation {
  title: string;
  url: string;
  /** A short extract of the cited page, shown under the title. */
  snippet: string;
}

/**
 * The "Sources" card for a web-search answer (BL-213 v2, L3 web knowledge).
 * Emitted by the BACKEND (not the model) from the `web_search` tool result, so
 * the URLs are always exactly what the search returned — never model-invented.
 * Renders like GPT's sources: a titled list of clickable links + snippets.
 */
export interface CoPilotSourcesEnvelope extends CoPilotEnvelopeBase {
  type: 'sources';
  sources: CoPilotWebCitation[];
}

/** What a partner offers from the CoPilot (InOut: the agency's two steps). */
export const COPILOT_PARTNER_OFFER_KINDS = ['CONSULTATION', 'REQUISITION'] as const;
export type CoPilotPartnerOfferKind = (typeof COPILOT_PARTNER_OFFER_KINDS)[number];

/** The name of each offer on the card — one key per kind, checked by the locale coverage spec. */
export const COPILOT_OFFER_KIND_LABEL_KEY = {
  CONSULTATION: 'copilot.offer.kind.CONSULTATION',
  REQUISITION: 'copilot.offer.kind.REQUISITION',
} as const satisfies Record<CoPilotPartnerOfferKind, string>;

/**
 * The partner offer card — the next step with a regulated partner (InOut: the
 * immigration agency). Emitted by the BACKEND, never the model, and only in a
 * domain whose profile declares a partner route (R2 — elsewhere D11 holds:
 * the Companion never routes to a person). The partner is named as the partner
 * registry's `displayName` says; its staff never. Prices are the country's, VAT
 * as stated. Each offer opens its page on the web — on the phone too (no
 * checkout in the app).
 */
export interface CoPilotOfferCardEnvelope extends CoPilotEnvelopeBase {
  type: 'offer_card';
  partner: { code: string; displayName: string; websiteUrl: string };
  offers: Array<{
    kind: CoPilotPartnerOfferKind;
    price: { amount: number; currency: string; vatIncluded: boolean };
    /** The web portal path that takes the step, e.g. `/inout/agency`. */
    webPath: string;
  }>;
  /** Paying for the consultation counts toward the requisition (the country's rule). */
  consultationCreditsRequisition: boolean;
}

export type CoPilotResponseEnvelope =
  | CoPilotTextEnvelope
  | CoPilotTaskCardEnvelope
  | CoPilotChecklistEnvelope
  | CoPilotScoreExplainerEnvelope
  | CoPilotKitItemEnvelope
  | CoPilotComparisonTableEnvelope
  | CoPilotDocumentSummaryEnvelope
  | CoPilotCaseInsightEnvelope
  | CoPilotProblemReportEnvelope
  | CoPilotActionProposalEnvelope
  | CoPilotEscalationCardEnvelope
  | CoPilotSourcesEnvelope
  | CoPilotOfferCardEnvelope;

export type CoPilotEnvelopeType = CoPilotResponseEnvelope['type'];
